import './globalHooks';

import { useCurrentClient, useCurrentRouteState, useHideClients, useMyClients } from '@pbk-web/state';
import { Roles, systemDefaultLanguageCode } from '@pbk-web/types';
import { PageLoader, useTheme } from '@pbk-web/ui';
import { ThemeUtils } from '@pbk-web/utils';
import type { ReactElement } from 'react';
import { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ProductFruits } from 'react-product-fruits';
import { useLocation } from 'react-router';
import { useShallow } from 'zustand/react/shallow';

import { FlagsProvider } from './contexts/FeatureFlagContext';
import { usePbkAuth } from './contexts/PbkAuthContext';
import { SignalRProcessesProvider } from './contexts/signalR/ProcessesContext';
import type { FeatureFlags } from './features/FeatureFlag';
import { useCurrentUser } from './global-state/Auth';
import { useInAdminSpace } from './global-state/Workspace';
import usePermissions from './hooks/permissions/usePermissions';
import { useCurrentRoute } from './hooks/useCurrentRoute';
import { useDetectProblematicConnection } from './hooks/useDetectProblematicConnection';
import useErrorInterceptor from './hooks/useErrorInterceptor';
import useFallbackLanguage from './hooks/useFallbackLanguage';
import { useNavigateRef } from './hooks/useNavigateRef';
import { useOnlineStatusCheck } from './hooks/useOnlineStatusCheck';
import { useSsoSignErrorNotice } from './hooks/useSsoSignErrorNotice';
import { AutoLayout } from './layouts/Layout';
import PbkErrorBoundary from './PbkErrorBoundry';
import Router from './Router';
import ClientService from './services/ClientService';
import CurrentUserService from './services/CurrentUserService';
import FeatureFlagService from './services/FeatureFlagService';
import { goToClientSpacePath } from './utils/NavigationUtils';

const App = (): ReactElement => {
  const [currentUser, setCurrentUser] = useCurrentUser(useShallow((x) => [x.value, x.setValue]));
  const currentClient = useCurrentClient((x) => x.value);
  const setHideClientsNav = useHideClients((x) => x.setValue);
  const setInAdminSpace = useInAdminSpace((x) => x.setValue);
  const location = useLocation();
  const { match: routeMatch, route: currentRoute } = useCurrentRoute();
  const setCurrentRoute = useCurrentRouteState((x) => x.setValue);
  const { isAuthenticated, refreshToken, features } = usePbkAuth();

  const [appReady, setAppReady] = useState(false);
  const navigate = useNavigateRef();
  const currentTheme = useTheme((x) => x.value);
  const hasPermission = usePermissions();
  const [myClients, setMyClients] = useMyClients(useShallow((x) => [x.value, x.setValue]));
  const [featureFlags, setFeatureFlags] = useState<FeatureFlags>(null!);
  const firstLoadCalled = useRef(false);
  const appInitCalled = useRef(false);
  const { i18n } = useTranslation();

  useDetectProblematicConnection();
  useOnlineStatusCheck();
  useSsoSignErrorNotice();

  const fallbackLanguage = useFallbackLanguage();

  useEffect(() => {
    currentTheme && ThemeUtils.applyTheme(currentTheme);
  }, [currentTheme]);

  useEffect(() => {
    if (firstLoadCalled.current || currentRoute?.publicPage) return;

    firstLoadCalled.current = true;
    if (features.unmanagedTokens) {
      refreshToken();
    }
  }, [currentRoute?.publicPage, features.unmanagedTokens, refreshToken]);

  useEffect(
    function setCurrentRouteState() {
      setCurrentRoute(currentRoute);
    },
    [currentRoute, setCurrentRoute],
  );

  useEffect(() => {
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  useEffect(() => {
    setInAdminSpace(location.pathname.startsWith('/admin/iam'));
  }, [location.pathname, setInAdminSpace]);

  const isConsultant = useMemo(() => hasPermission(Roles.Consultant), [hasPermission]);

  // Set currentUser atom. Runs once per authenticated session: re-fetching the
  // profile here on navigation re-applied a possibly-stale `profile.language`,
  // racing (and reverting) an in-flight language-change PUT.
  useEffect(
    function appInit() {
      if (!isAuthenticated) {
        // Allow re-init after logout so the next login re-fetches the profile
        // and re-applies its language.
        appInitCalled.current = false;
        setAppReady(true);
        return;
      }

      if (appInitCalled.current) return;
      appInitCalled.current = true;

      FeatureFlagService.getFeatureFlagStatusses()
        .then((res) => setFeatureFlags(res.data.flags))
        .catch(() =>
          setFeatureFlags({
            moduleSharing: false,
            dataExport: false,
            distributionList: false,
            knowledgeGraph: false,
            templateReferenceAction: false,
            formsV2: false,
          }),
        );

      CurrentUserService.getProfile()
        .then((res) => {
          localStorage.removeItem('auth-user');
          const profile = res.data.user;
          profile.loggedInMethod = res.data.loggedInMethod;
          const locale = profile?.language || systemDefaultLanguageCode;
          i18n.changeLanguage(locale);

          setCurrentUser((prev) => ({
            ...prev,
            ...profile,
          }));

          setAppReady(true);

          ClientService.getMyClients().then((res) => {
            setMyClients(res.data);
          });
        })
        .catch(() => {
          // Clear the guard so a transient profile-fetch failure doesn't
          // permanently block init; a later effect run can retry while
          // authenticated. Success keeps the guard set (see .then above).
          appInitCalled.current = false;
          // show error message
          setAppReady(true);
        });
    },
    [i18n, isAuthenticated, setCurrentUser, setMyClients],
  );

  // Hide Clients nav menu item when not a consultant and there is only one client.
  // Kept reactive to isConsultant/myClients so appInit can stay run-once.
  useEffect(() => {
    if (!myClients) return;
    setHideClientsNav(!isConsultant && myClients.length === 1);
  }, [isConsultant, myClients, setHideClientsNav]);

  // Redirect single-client non-consultants from /clients or / to their client home
  useEffect(() => {
    if (!isAuthenticated || isConsultant || !currentUser || !appReady) return;

    if (myClients.length !== 1) return;

    const isOnClientsListOrRoot = location.pathname === '/clients' || location.pathname === '/';
    if (isOnClientsListOrRoot && routeMatch?.params.clientId !== myClients[0].id) {
      navigate.current(goToClientSpacePath(myClients[0], currentUser), { replace: true });
    }
  }, [isAuthenticated, isConsultant, currentUser, appReady, myClients, location.pathname, routeMatch?.params.clientId, navigate]);

  useErrorInterceptor();

  const userInfo = useMemo(() => {
    if (currentUser) {
      return {
        username: currentUser.id,
        email: currentUser.email,
        firstname: currentUser.firstName,
        lastname: currentUser.lastName,
        props: {
          company: currentClient?.name ?? '(unknown)',
          environment: window.location.hostname,
        },
      };
    }

    return { username: 'unknown' };
  }, [currentUser, currentClient]);

  return (
    <PbkErrorBoundary currentClient={currentClient} currentUser={currentUser}>
      <PageLoader loading={!appReady}>
        <SignalRProcessesProvider>
          <div className={`flex h-screen bg-white font-sans text-black ${currentTheme?.name}`}>
            <Suspense fallback={<PageLoader loading isSuspense />}>
              <AutoLayout>
                <FlagsProvider featureFlags={featureFlags}>
                  <Router />
                </FlagsProvider>
              </AutoLayout>
              {import.meta.env.VITE_PRODUCT_FRUIT_WORKSPACE_CODE && currentUser?.language && (
                <ProductFruits
                  key={`${currentUser?.language}`}
                  workspaceCode={import.meta.env.VITE_PRODUCT_FRUIT_WORKSPACE_CODE}
                  language={currentUser?.language || i18n.language || fallbackLanguage}
                  user={userInfo}
                  lifeCycle={'unmount'}
                />
              )}
            </Suspense>
          </div>
        </SignalRProcessesProvider>
      </PageLoader>
    </PbkErrorBoundary>
  );
};

export default App;
